import { useState } from 'react'

import {
  Box,
  Checkbox,
  Icon,
  Text,
  Tooltip,
  useBreakpoint,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { ChevronLeft } from '@factorialco/f0-react/icons/app'
import {
  CreateSelfUpsellDocument,
  EstimateProrateDocument,
  NextInvoiceDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import LeftSection from 'modules/upsell/components/SelfUpsell/CheckoutModal/LeftSection'
import PaymentDetail from 'modules/upsell/components/SelfUpsell/CheckoutModal/PaymentDetail'
import useSelfUpsellPrices from 'modules/upsell/hooks/useSelfUpsellPrices'
import { LEGAL_LINKS } from 'modules/upsell/lib/legalLinks'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { formatDate } from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type TermsAndConditionsProps = {
  hasAcceptedTerms: boolean
  setHasAcceptedTerms: (hasAcceptedTerms: boolean) => void
  locale: string
  checkboxError: boolean
  setCheckboxError: (checkboxError: boolean) => void
}

const TermsAndConditions = ({
  hasAcceptedTerms,
  setHasAcceptedTerms,
  locale,
  checkboxError,
  setCheckboxError,
}: TermsAndConditionsProps) => {
  const getLegalLinks = (): { terms: string; privacy: string } => {
    if (LEGAL_LINKS[locale]) return LEGAL_LINKS[locale]
    const baseLocale = locale.split('-')[0] || 'en'

    return (
      LEGAL_LINKS[baseLocale] || {
        terms: 'https://factorialhr.com/terms-of-use',
        privacy: 'https://factorialhr.com/privacy',
      }
    )
  }

  const { terms, privacy } = getLegalLinks()

  const handleCheckboxChange = () => {
    setHasAcceptedTerms(!hasAcceptedTerms)
    setCheckboxError(false)
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="flexStart">
      <Checkbox checked={hasAcceptedTerms} onChange={handleCheckboxChange} size="large" />
      <Box alignSelf="start">
        <Box gap="s4">
          <Text.Normal weight="normal" color="grey800" size="200">
            {i18n.t('self_upsell.second_step.terms_checkbox_1')}{' '}
            <span
              onClick={() => {
                window.open(terms, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_2')}
              </Text.Normal>
            </span>{' '}
            {i18n.t('self_upsell.second_step.terms_checkbox_3')}{' '}
            <span
              onClick={() => {
                window.open(privacy, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_4')}
              </Text.Normal>
            </span>
            .
          </Text.Normal>
          {checkboxError && (
            <Text.Normal color="moodSuperNegative" size="100">
              {i18n.t('self_upsell.second_step.terms_checkbox_error')}
            </Text.Normal>
          )}
        </Box>
      </Box>
    </Box>
  )
}

type SecondStepProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  step: string
  setStep: (step: string) => void
  moduleName: string
}

const SecondStep = ({ addon, crossSellingAddon, step, setStep, moduleName }: SecondStepProps) => {
  const queryParams = new URLSearchParams(location.search)
  const upsellOrigin = queryParams.get('upsell_origin')
  const { company } = useCurrentSession()
  const [open, toggle] = useToggle(false)
  const { isMobile } = useBreakpoint()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [checkboxError, setCheckboxError] = useState(false)
  const [proratedPrice, setProratedPrice] = useState<{
    amount: number
    currency: string
    discounts: {
      discountPercentage: number
      amount: number
      discountType: string
    }[]
  } | null>(null)
  const { configPlan, period, currency, seats, shouldBypassSelfUpsell, currentSeats, isExpenses } =
    useSelfUpsellContext()
  const { perSeatPrice, feePrice, stairstepPrice, totalPriceAfterDiscount } = useSelfUpsellPrices({
    addon,
    discounts: proratedPrice?.discounts,
  })
  const [fetchNextInvoiceData, { data: nextInvoiceData }] = useLazyQuery(NextInvoiceDocument)
  const nextInvoiceDate =
    nextInvoiceData?.billing.cpNextBillingEstimatesConnection.nodes[0]?.subscriptionEstimate
      ?.nextBillingAt

  const today = new Date()
  const formattedToday = formatDate(today)
  const billingDate = nextInvoiceDate ? new Date(nextInvoiceDate * 1000) : new Date()
  const formattedBillingDate = formatDate(billingDate)
  const tooltipLabel = `${i18n.t(
    'self_upsell.second_step.tooltip'
  )} ${formattedToday} - ${formattedBillingDate}. ${i18n.t(
    'self_upsell.second_step.prorated_description'
  )}`

  const [estimateProrate, { loading }] = useMutation(EstimateProrateDocument, {
    extractorFn: (data) => data.billingMutations.createBillingEstimateProrate,
  })

  const [selfUpsellSubscription] = useMutation(CreateSelfUpsellDocument, {
    extractorFn: (data) => data.billingMutations.createBillingUpgradeSubscription,
  })

  const getEstimateProrate = async () => {
    if (!configPlan.addon) return

    const result = await estimateProrate({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem,
              seats: isExpenses() ? seats : currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon,
                seats: isExpenses() ? seats : currentSeats,
                module: moduleName,
              },
            ],
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      return {
        amount: handled.data?.estimateProrate?.amount,
        currency: handled.data?.estimateProrate?.currency,
        discounts: handled.data?.estimateProrate?.discounts,
      }
    }
  }

  const trackEvent = () => {
    track(trackingEvents.upsell.clickConfirmPaymentButton, {
      module: moduleName,
      selectedPlan: configPlan.variant,
      seats: seats,
      billingPeriod: period,
      upsellAmount: totalPriceAfterDiscount,
      proratedAmount: proratedPrice?.amount,
      upsellOrigin: upsellOrigin,
    })
  }

  const handleConfirm = async () => {
    setCheckboxError(false)
    if (!hasAcceptedTerms) setCheckboxError(true)
    if (!configPlan.addon || !hasAcceptedTerms || !configPlan.variant) return
    if (shouldBypassSelfUpsell) {
      setStep('success')
      return
    }

    trackEvent()

    const result = await selfUpsellSubscription({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem,
              seats: isExpenses() ? seats : currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon,
                seats: isExpenses() ? seats : currentSeats,
                module: moduleName,
              },
            ],
        origin: 'Self upsell - Marketplace detail page',
        upsellInfo: {
          itemName: `${addon.title} ${configPlan.variant}`,
          moduleRequested: moduleName,
          pricePerSeat: perSeatPrice === 0 ? undefined : perSeatPrice,
          priceFixed: feePrice === 0 ? undefined : feePrice,
          priceStairstep: stairstepPrice === 0 ? undefined : stairstepPrice,
          proratedPrice: proratedPrice?.amount ? Number(proratedPrice.amount) : undefined,
          seats: isExpenses() ? seats : currentSeats,
          currency: currency,
          totalPrice: totalPriceAfterDiscount,
          billingDate: formattedBillingDate,
          billingPeriod: period,
        },
        crossSellingInfo: crossSellingAddon?.isSelected
          ? {
              itemName: `${crossSellingAddon?.title}`,
              moduleRequested: 'one',
              pricePerSeat: perSeatPrice === 0 ? undefined : perSeatPrice,
              priceFixed: feePrice === 0 ? undefined : feePrice,
              priceStairstep: stairstepPrice === 0 ? undefined : stairstepPrice,
              proratedPrice: 0,
              seats: isExpenses() ? seats : currentSeats,
              currency: currency,
              totalPrice: 0,
              billingDate: formattedBillingDate,
              billingPeriod: period,
            }
          : undefined,
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      setStep('success')
    } else {
      setStep('error')
    }
  }

  useEffect(() => {
    if (shouldBypassSelfUpsell) return
    getEstimateProrate().then((data) => {
      if (!data) return
      setProratedPrice({
        amount: data.amount ?? 0,
        currency: data.currency ?? currency,
        discounts:
          data.discounts
            ?.filter((d) => d.discountPercentage && d.amount)
            .map((d) => ({
              discountPercentage: d.discountPercentage ?? 0,
              amount: d.amount ?? 0,
              discountType: d.discountType ?? '',
            })) ?? [],
      })
    })
    fetchNextInvoiceData({
      variables: { companyId: company.id },
    })
  }, [seats, currency, shouldBypassSelfUpsell])
  return (
    <Box flexDirection="row" height="full">
      {!isMobile && (
        <LeftSection addon={addon} variant={configPlan.variant} step={step} iconName={moduleName} />
      )}
      <Box
        padding="s24"
        justifyContent="spaceBetween"
        style={{
          width: isMobile ? '100%' : '60%',
        }}
      >
        <Box gap="s16">
          <Box height="s24" />
          {isMobile && (
            <Text size="400" color="grey1000" weight="semibold">
              {addon.title}
            </Text>
          )}
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('self_upsell.second_step.order_summary')}
          </Text.Normal>
          <PaymentDetail
            addon={addon}
            crossellingAddon={crossSellingAddon}
            billingDate={formattedBillingDate}
            discounts={proratedPrice?.discounts}
            loadingPrice={loading}
          />
          <TermsAndConditions
            hasAcceptedTerms={hasAcceptedTerms}
            setHasAcceptedTerms={setHasAcceptedTerms}
            locale={company.locale || 'en'}
            checkboxError={checkboxError}
            setCheckboxError={setCheckboxError}
          />
        </Box>
        <Box
          gap="s16"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          flexWrap="wrap"
          style={{
            height: '56px',
          }}
        >
          <Box gap="s4">
            <Box flexDirection="row" alignItems="center" gap="s4">
              <Text.Normal size="200" color="grey700" weight="medium">
                {i18n.t('self_upsell.common.amount_today')}
              </Text.Normal>
              <Box alignSelf="center">
                <Tooltip.Alt
                  size="small"
                  attachment="bottom center"
                  open={open}
                  toggle={toggle}
                  label={tooltipLabel}
                >
                  <Icon.Medium icon="info-circle-line" />
                </Tooltip.Alt>
              </Box>
            </Box>
            <Text size="400" color="grey1000" weight="semibold">
              {loading ? (
                <Spinner size="small" />
              ) : proratedPrice ? (
                formatPrice(proratedPrice.amount, proratedPrice.currency)
              ) : (
                '-'
              )}
            </Text>
          </Box>
          <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
            <F0Button
              hideLabel
              icon={ChevronLeft}
              variant="outline"
              round
              size="lg"
              onClick={() => setStep('first')}
            />
            <F0Button
              size="lg"
              label={i18n.t('self_upsell.second_step.button')}
              onClick={handleConfirm}
              disabled={!configPlan.addon}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SecondStep
